import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, FileText, Loader2, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { getApiBaseUrl } from '../../utils/apiUrl';

const LeagueImportModal = ({ isOpen, onClose, leagues, onSuccess, showNotification }) => {
  const [file, setFile] = useState(null);
  const [selectedLeague, setSelectedLeague] = useState('');
  const [importType, setImportType] = useState('rankings'); // 'rankings' or 'teams'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = importType === 'rankings' 
        ? ['text/csv', 'application/vnd.ms-excel']
        : ['application/json'];
      
      if (!validTypes.includes(selectedFile.type) && !selectedFile.name.endsWith(importType === 'rankings' ? '.csv' : '.json')) {
        showNotification('กรุณาเลือกไฟล์ ' + (importType === 'rankings' ? 'CSV' : 'JSON'), 'error');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      showNotification('กรุณาเลือกไฟล์', 'error');
      return;
    }

    if (importType === 'rankings' && !selectedLeague) {
      showNotification('กรุณาเลือกลีก', 'error');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('กรุณาเข้าสู่ระบบก่อน');
      }

      const formData = new FormData();
      // Backend expects 'csvFile' field name
      formData.append('csvFile', file);
      
      if (importType === 'rankings') {
        formData.append('leagueId', selectedLeague);
        formData.append('sourceId', '1'); // Default source ID
      }

      const endpoint = importType === 'rankings'
        ? `${getApiBaseUrl()}/api/gbl/import/${selectedLeague}`
        : `${getApiBaseUrl()}/api/teams/import`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Backend returns data.data or data.stats
        const backendStats = data.data || data.stats || {};
        setResult({
          success: true,
          message: data.message || 'นำเข้าข้อมูลสำเร็จ',
          stats: {
            successCount: backendStats.successfulRecords || backendStats.successCount || 0,
            errorCount: backendStats.failedRecords || backendStats.errorCount || 0,
            totalRecords: backendStats.totalRecords || 0
          },
          errors: backendStats.errors || []
        });
        showNotification(data.message || 'นำเข้าข้อมูลสำเร็จ', 'success');
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 2000);
        }
      } else {
        setResult({
          success: false,
          message: data.error || data.message || 'นำเข้าข้อมูลไม่สำเร็จ',
          errors: data.errors || []
        });
        showNotification(data.error || data.message || 'นำเข้าข้อมูลไม่สำเร็จ', 'error');
      }
    } catch (error) {
      setResult({
        success: false,
        message: 'เกิดข้อผิดพลาด: ' + error.message
      });
      showNotification('เกิดข้อผิดพลาด: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    if (importType === 'rankings') {
      // CSV template for rankings - must match backend expected columns
      const csvContent = `Pokemon,Score,Dex,Type 1,Type 2,Attack,Defense,Stamina,Stat Product,Level,CP,Fast Move,Charged Move 1,Charged Move 2,Charged Move 1 Count,Charged Move 2 Count,Buddy Distance,Charged Move Cost
Pikachu,95.5,25,Electric,,112,96,111,1192320,20,1500,Thunder Shock,Thunderbolt,Wild Charge,8,5,0,0
Charizard,92.3,6,Fire,Flying,223,173,186,7173042,50,2500,Fire Spin,Blast Burn,Dragon Claw,3,2,0,0`;
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'league_rankings_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      // JSON template for teams
      const jsonContent = {
        leagueId: 1,
        teams: [
          {
            name: "Team 1",
            pokemon: [
              {
                speciesId: "pikachu",
                fastMove: "thunder_shock",
                chargedMoves: ["thunderbolt", "wild_charge"],
                weight: 1.0
              }
            ]
          }
        ]
      };
      
      const blob = new Blob([JSON.stringify(jsonContent, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'league_teams_template.json';
      a.click();
      window.URL.revokeObjectURL(url);
    }
    showNotification('ดาวน์โหลดเทมเพลตสำเร็จ', 'success');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">นำเข้าข้อมูลลีก</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Import Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              ประเภทข้อมูล
            </label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => {
                  setImportType('rankings');
                  setFile(null);
                  setResult(null);
                }}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                  importType === 'rankings'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                }`}
              >
                <FileText className="w-5 h-5 mx-auto mb-2 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium">Rankings (CSV)</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setImportType('teams');
                  setFile(null);
                  setResult(null);
                }}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                  importType === 'teams'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                }`}
              >
                <FileText className="w-5 h-5 mx-auto mb-2 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium">Teams (JSON)</span>
              </button>
            </div>
          </div>

          {/* League Selection (for rankings) */}
          {importType === 'rankings' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                เลือกลีก *
              </label>
              <select
                value={selectedLeague}
                onChange={(e) => setSelectedLeague(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">-- เลือกลีก --</option>
                {leagues.filter(l => l.is_active).map(league => (
                  <option key={league.id} value={league.id}>
                    {league.name} ({league.name_th})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              เลือกไฟล์ {importType === 'rankings' ? 'CSV' : 'JSON'} *
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
              <input
                type="file"
                accept={importType === 'rankings' ? '.csv' : '.json'}
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <Upload className="w-12 h-12 text-gray-400 mb-4" />
                <span className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {file ? file.name : `คลิกเพื่อเลือกไฟล์ ${importType === 'rankings' ? 'CSV' : 'JSON'}`}
                </span>
                <span className="text-xs text-gray-500">
                  หรือลากไฟล์มาวางที่นี่
                </span>
              </label>
            </div>
          </div>

          {/* Template Download */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                ต้องการเทมเพลต?
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                ดาวน์โหลดไฟล์ตัวอย่างเพื่อดูรูปแบบข้อมูล
              </p>
            </div>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>ดาวน์โหลดเทมเพลต</span>
            </button>
          </div>

          {/* Result */}
          {result && (
            <div className={`p-4 rounded-lg ${
              result.success
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700'
            }`}>
              <div className="flex items-start space-x-3">
                {result.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    result.success
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-red-800 dark:text-red-200'
                  }`}>
                    {result.message}
                  </p>
                  {result.stats && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      <p>สำเร็จ: {result.stats.successCount || 0} รายการ</p>
                      <p>ล้มเหลว: {result.stats.errorCount || 0} รายการ</p>
                    </div>
                  )}
                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                      <p className="font-medium">ข้อผิดพลาด:</p>
                      <ul className="list-disc list-inside mt-1">
                        {result.errors.slice(0, 5).map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              ยกเลิก
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={loading || !file || (importType === 'rankings' && !selectedLeague)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>กำลังนำเข้า...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>นำเข้าข้อมูล</span>
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LeagueImportModal;
